import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Alert, Radio, RadioGroup, FormControlLabel, LinearProgress, CircularProgress, GlobalStyles } from "@mui/material";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import SelfTestApi, { SelfTestDetail } from "../api/SelfTestApi";
import { useAuth } from "../context/AuthContext";

// Print CSS scoped to the answers overview: everything else is hidden so
// "Afdrukken" produces only the questionnaire answers + attribution.
const printStyles = (
  <GlobalStyles
    styles={{
      "@media print": {
        "body *": { visibility: "hidden" },
        ".selftest-print, .selftest-print *": { visibility: "visible" },
        ".selftest-print": { position: "absolute", left: 0, top: 0, width: "100%", padding: "24px" },
        ".selftest-no-print": { display: "none !important" },
      },
    }}
  />
);

// Informative questionnaire (MDR-safe): after completion we show a uniform
// closing card — identical regardless of the answers — and list the user's own
// answers verbatim. Deliberately NO computation, banding, highlighting or
// interpretation, mirroring the sister project vind-hulp. Answers never leave
// the browser.
export default function SelfTest() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [test, setTest] = useState<SelfTestDetail | null>(null);
  // "locked": reflectie-vragenlijst zonder toewijzing (API antwoordt 401/403
  // met code "assignment_required") — nette uitleg i.p.v. de vragen.
  const [locked, setLocked] = useState(false);
  const [answers, setAnswers] = useState<number[]>([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLocked(false);
    SelfTestApi.get(slug).then((d) => { setTest(d); setAnswers(new Array(d.questions.length).fill(-1)); }).catch((e: any) => {
      const status = e?.response?.status;
      if (status === 401 || status === 403) setLocked(true);
    });
  }, [slug, user]);

  if (locked) {
    return (
      <Box sx={{ bgcolor: "background.default" }}>
        <Container maxWidth="sm" sx={{ py: { xs: 6, sm: 10 } }}>
          <Box sx={{ p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
            <Typography variant="h1" sx={{ fontSize: { xs: "1.5rem", sm: "1.8rem" }, mb: 1.5 }}>{t("selftest.lockedTitle")}</Typography>
            <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("selftest.lockedBody")}</Typography>
            {!user && <Alert severity="info" sx={{ mb: 2 }}>{t("selftest.lockedLogin")}</Alert>}
            <Button variant="contained" onClick={() => navigate("/zelfhulp")}>{t("selftest.lockedCta")}</Button>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!test) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;

  const answered = answers.filter((a) => a >= 0).length;
  const allAnswered = answered === test.questions.length;

  const finish = () => { setDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const retake = () => { setDone(false); setAnswers(new Array(test.questions.length).fill(-1)); };
  const completedOn = new Date().toLocaleDateString(i18n.language === "fr" ? "fr-BE" : i18n.language === "en" ? "en-GB" : "nl-BE", { day: "numeric", month: "long", year: "numeric" });

  return (
    <Box sx={{ bgcolor: "background.default", pb: 10 }}>
      {printStyles}
      <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
        <Typography variant="h1" className="selftest-no-print" sx={{ fontSize: { xs: "2rem", sm: "2.5rem" }, mb: 1 }}>{test.title}</Typography>

        {done ? (
          <Box sx={{ mt: 3 }}>
            {/* Uniform closing card — identical for every answer combination. */}
            <Box className="selftest-no-print" sx={{ p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
              <Typography variant="h2" sx={{ fontSize: "1.6rem", mb: 1 }}>{t("selftest.closingTitle")}</Typography>
              <Typography sx={{ mb: 2 }}>{t("selftest.closingBody")}</Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button variant="contained" onClick={() => navigate("/start")}>{t("selftest.ctaMatch")}</Button>
                <Button variant="text" onClick={retake}>{t("selftest.retake")}</Button>
              </Box>
            </Box>

            {/* Crisis block — always visible on the closing screen. */}
            <Alert severity="warning" className="selftest-no-print" sx={{ mt: 3 }}>
              {t("selftest.crisisIntro")}{" "}
              <Box component="a" href="tel:1813" sx={{ color: "inherit", fontWeight: 600 }}>{t("selftest.crisis1813")}</Box>{" "}
              {t("selftest.crisis112")}
            </Alert>

            {/* Verbatim answers overview: numbered question + chosen label, zero
                computation or highlighting. This is the only printed section. */}
            <Box className="selftest-print" sx={{ mt: 3, p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
              <Typography variant="h3" sx={{ fontSize: "1.15rem", mb: 1 }}>{t("selftest.answersTitle")}</Typography>
              <Typography sx={{ fontSize: ".95rem", color: "text.secondary", mb: 1.5 }}>{t("selftest.answersLead")}</Typography>
              <Typography sx={{ fontSize: ".85rem", color: "text.secondary", mb: 2 }}>
                {test.title} — {t("selftest.completedOn", { date: completedOn })}
              </Typography>

              <Box component="ol" sx={{ m: 0, pl: 3, display: "grid", gap: 1.5 }}>
                {test.questions.map((q, i) => (
                  <Box component="li" key={i} sx={{ breakInside: "avoid" }}>
                    <Typography sx={{ fontWeight: 600, m: 0 }}>{q.text}</Typography>
                    <Typography sx={{ m: 0, color: "text.secondary" }}>{test.scaleLabels[answers[i]] ?? "—"}</Typography>
                  </Box>
                ))}
              </Box>

              {test.source && (
                <Typography sx={{ fontSize: ".82rem", color: "text.secondary", mt: 2.5, mb: 0 }}>
                  {t("selftest.sourceLabel")}: {test.source}
                </Typography>
              )}
              <Typography sx={{ fontSize: ".82rem", color: "text.secondary", mt: 0.5, mb: 2 }}>{t("selftest.printFooter")}</Typography>

              <Button className="selftest-no-print" variant="outlined" startIcon={<PrintRoundedIcon />} onClick={() => window.print()}>
                {t("selftest.print")}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: "1.05rem", mb: 3 }}>{test.intro}</Typography>
            <LinearProgress variant="determinate" value={(answered / test.questions.length) * 100} sx={{ height: 8, borderRadius: 999, bgcolor: "var(--primary-100)", mb: 4, "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "primary.main" } }} />

            {test.questions.map((q, i) => (
              <Box key={i} sx={{ p: { xs: 2.5, sm: 3 }, mb: 2, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
                <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{i + 1}. {q.text}</Typography>
                <RadioGroup value={String(answers[i])} onChange={(e) => setAnswers((a) => { const n = [...a]; n[i] = Number(e.target.value); return n; })}>
                  {test.scaleLabels.map((label, v) => (
                    <FormControlLabel key={v} value={String(v)} control={<Radio size="small" />} label={label} />
                  ))}
                </RadioGroup>
              </Box>
            ))}

            <Button variant="contained" size="large" fullWidth onClick={finish} disabled={!allAnswered} sx={{ mt: 2 }}>
              {t("selftest.submit")}
            </Button>
          </>
        )}
      </Container>
    </Box>
  );
}
